import { useState } from "react";
import { useStore } from "reactflow";
import { useAuthStore } from "../../../store/storeAuth";
import { alpha } from "@mui/material/styles";
import { CSSProperties } from "react";
import { shallow } from "zustand/shallow";
import { HandleId, UseHandleConnectionProps } from "./types.d";

export const useHandleConnection = ({
    handleId,
    validSourceHandles,
    isActive = false,
    colorActive,
    side,
}: UseHandleConnectionProps) => {
    const { user } = useAuthStore((state) => state);
    const [isHovered, setIsHovered] = useState(false);

    const { isDraggingConnection, sourceHandleId } = useStore(
        (s) => ({
            isDraggingConnection: s.connectionNodeId !== null,
            sourceHandleId: s.connectionStartHandle?.handleId,
        }),
        shallow,
    );

    const isHandlesSameType = sourceHandleId === handleId;
    const isValidSourceHandle = sourceHandleId && validSourceHandles.some(handle => sourceHandleId?.startsWith(handle));
    const isDark = user?.theme === "dark";
    const isDropTarget =
        isDraggingConnection && isHovered && isValidSourceHandle && !isHandlesSameType;

    const backgroundColor = isDropTarget || isActive
        ? colorActive?.color
        : isDark ? "#1D2939" : "#fff";

    const baseStyle: CSSProperties = {
        width: isActive ? "32px" : "29px",
        height: isActive ? "32px" : "29px",
        border: isDropTarget
            ? `2px solid ${colorActive?.color}`
            : isActive
                ? "0px"
                : "2px solid #667085",
        borderColor: isDropTarget || isActive ? "none" : "#667085",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        [side === "left" ? "left" : "right"]: "-16px",
        transform: `translateY(-16.5px) scale(${isDropTarget ? "1.25" : "1"})`,
        boxShadow: isDropTarget
            ? `0 0 0 4px ${alpha(colorActive?.color || "#3650DE", 0.25)}, 0 0 12px ${alpha(colorActive?.color || "#3650DE", 1)}`
            : undefined,
        transition: "all 100ms ease",
        ...(isDraggingConnection && !isValidSourceHandle && { cursor: "not-allowed" }),
    };

    const handleMouseEnter = () => {
        if (isDraggingConnection && !isHandlesSameType) setIsHovered(true);
    };

    const handleMouseLeave = () => setIsHovered(false);

    return {
        isDropTarget,
        isDark,
        backgroundColor,
        baseStyle,
        handleMouseEnter,
        handleMouseLeave,
    };
};
